import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useExchangeRate } from '../contexts/ExchangeRateContext';
import { getStores } from '../api/client';
import { 
  createInitialInventoryValue, 
  getInitialInventoryValues, 
  updateInitialInventoryValue, 
  deleteInitialInventoryValue,
  getStoreFinancialBalance
} from '../api/client';
import { 
  PlusIcon, 
  TrashIcon, 
  PencilIcon, 
  CheckIcon, 
  XMarkIcon,
  ArchiveBoxIcon,
  ChartBarIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../utils/formatters';

const InitialInventoryValues = () => {
  const { user } = useAuth();
  const { exchangeRate } = useExchangeRate();
  const [stores, setStores] = useState([]);
  const [inventoryValues, setInventoryValues] = useState([]);
  const [balanceData, setBalanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingData, setEditingData] = useState(null);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [newInventoryValue, setNewInventoryValue] = useState({
    store_id: '',
    period_month: currentMonth,
    period_year: currentYear,
    initial_value: '',
    currency: 'USD',
    notes: ''
  });

  const [editInventoryValue, setEditInventoryValue] = useState({
    store_id: '',
    period_month: currentMonth,
    period_year: currentYear,
    initial_value: '',
    currency: 'USD',
    notes: ''
  });

  const [balanceFilters, setBalanceFilters] = useState({
    store_id: '',
    period_month: currentMonth,
    period_year: currentYear
  });

  const months = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ];

  useEffect(() => {
    loadStores();
    loadInventoryValues();
  }, []);

  const loadStores = async () => {
    try {
      const storesData = await getStores();
      setStores(storesData);
    } catch (err) {
      console.error('Error loading stores:', err);
    }
  };

  const loadInventoryValues = async () => {
    try {
      setLoading(true);
      const data = await getInitialInventoryValues();
      setInventoryValues(data);
      setError('');
    } catch (err) {
      console.error('Error loading initial inventory values:', err);
      setError('Error al cargar los valores iniciales de inventario');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInventoryValue = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setError('');
    setSuccess('');

    try {
      const amount = parseFloat(newInventoryValue.initial_value);
      const currentExchangeRate = exchangeRate || 1;
      
      // Calculate amounts in both currencies
      let initial_value_usd, initial_value_ves;
      if (newInventoryValue.currency === 'USD') {
        initial_value_usd = amount;
        initial_value_ves = amount * currentExchangeRate;
      } else {
        initial_value_ves = amount;
        initial_value_usd = amount / currentExchangeRate;
      }

      const dataToSubmit = {
        store_id: newInventoryValue.store_id,
        period_month: newInventoryValue.period_month,
        period_year: newInventoryValue.period_year,
        initial_value_usd: parseFloat(initial_value_usd.toFixed(2)),
        initial_value_ves: parseFloat(initial_value_ves.toFixed(2)),
        exchange_rate_used: currentExchangeRate,
        currency: newInventoryValue.currency,
        notes: newInventoryValue.notes || null
      };

      await createInitialInventoryValue(dataToSubmit);
      setSuccess('Valor inicial de inventario creado exitosamente');
      setNewInventoryValue({
        store_id: '',
        period_month: currentMonth,
        period_year: currentYear,
        initial_value: '',
        currency: 'USD',
        notes: ''
      });
      setShowCreateModal(false);
      loadInventoryValues();
    } catch (err) {
      console.error('Error creating initial inventory value:', err);
      setError(err.response?.data?.detail || 'Error al crear el valor inicial de inventario');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditInventoryValue = async (e) => {
    e.preventDefault();
    if (!editingData) return;
    
    setEditLoading(true);
    setError('');
    setSuccess('');

    try {
      const amount = parseFloat(editInventoryValue.initial_value);
      const currentExchangeRate = exchangeRate || 1;
      
      // Calculate amounts in both currencies
      let initial_value_usd, initial_value_ves;
      if (editInventoryValue.currency === 'USD') {
        initial_value_usd = amount;
        initial_value_ves = amount * currentExchangeRate;
      } else {
        initial_value_ves = amount;
        initial_value_usd = amount / currentExchangeRate;
      }

      const dataToSubmit = {
        store_id: editInventoryValue.store_id,
        period_month: editInventoryValue.period_month,
        period_year: editInventoryValue.period_year,
        initial_value_usd: parseFloat(initial_value_usd.toFixed(2)),
        initial_value_ves: parseFloat(initial_value_ves.toFixed(2)),
        exchange_rate_used: currentExchangeRate,
        currency: editInventoryValue.currency,
        notes: editInventoryValue.notes || null
      };

      await updateInitialInventoryValue(editingData.id, dataToSubmit);
      setSuccess('Valor inicial de inventario actualizado exitosamente');
      setShowEditModal(false);
      setEditingData(null);
      loadInventoryValues();
    } catch (err) {
      console.error('Error updating initial inventory value:', err);
      setError(err.response?.data?.detail || 'Error al actualizar el valor inicial de inventario');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteInventoryValue = async (inventoryId, storeName, month, year) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar el valor inicial de inventario para "${storeName}" - ${month}/${year}?`)) {
      return;
    }

    try {
      await deleteInitialInventoryValue(inventoryId);
      setSuccess('Valor inicial de inventario eliminado exitosamente');
      loadInventoryValues();
    } catch (err) {
      console.error('Error deleting initial inventory value:', err);
      setError('Error al eliminar el valor inicial de inventario');
    }
  };

  const handleGetBalance = async () => {
    if (!balanceFilters.store_id) {
      setError('Por favor selecciona una tienda para el análisis de balance');
      return;
    }

    setBalanceLoading(true);
    setError('');

    try {
      const balanceResult = await getStoreFinancialBalance(
        balanceFilters.store_id,
        balanceFilters.period_month,
        balanceFilters.period_year
      );
      setBalanceData(balanceResult);
      setSuccess('Balance financiero calculado exitosamente');
    } catch (err) {
      console.error('Error calculating financial balance:', err);
      setError(err.response?.data?.detail || 'Error al calcular el balance financiero');
      setBalanceData(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  const openEditModal = (data) => {
    setEditingData(data);
    const primaryAmount = data.currency === 'USD' ? data.initial_value_usd : data.initial_value_ves;
    setEditInventoryValue({
      store_id: data.store_id,
      period_month: data.period_month,
      period_year: data.period_year,
      initial_value: primaryAmount.toString(),
      currency: data.currency,
      notes: data.notes || ''
    });
    setShowEditModal(true);
    resetMessages();
  };

  const resetMessages = () => {
    setError('');
    setSuccess('');
  };

  const getStoreName = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store ? store.name : 'Tienda Desconocida';
  };

  const getMonthName = (monthNumber) => {
    const month = months.find(m => m.value === monthNumber);
    return month ? month.label : monthNumber.toString();
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'Healthy': return 'text-green-600 bg-green-100';
      case 'Caution': return 'text-yellow-600 bg-yellow-100';
      case 'Critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between mb-8">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            <ArchiveBoxIcon className="inline-block w-8 h-8 mr-3 text-blue-600" />
            Valores Iniciales de Inventario
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Registra el valor del inventario al inicio de cada mes para control financiero
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={() => {
              resetMessages();
              setShowCreateModal(true);
            }}
            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuevo Valor Inicial
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XMarkIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <CheckIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Balance Analysis Section */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            <ScaleIcon className="inline-block w-5 h-5 mr-2" />
            Balance Financiero: Inventario vs Cuentas por Pagar
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Tienda</label>
              <select
                value={balanceFilters.store_id}
                onChange={(e) => setBalanceFilters({...balanceFilters, store_id: e.target.value})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Seleccionar tienda</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Mes</label>
              <select
                value={balanceFilters.period_month}
                onChange={(e) => setBalanceFilters({...balanceFilters, period_month: parseInt(e.target.value)})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Año</label>
              <select
                value={balanceFilters.period_year}
                onChange={(e) => setBalanceFilters({...balanceFilters, period_year: parseInt(e.target.value)})}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {[2024, 2025, 2026].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={handleGetBalance}
                disabled={balanceLoading}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                {balanceLoading ? 'Calculando...' : 'Calcular Balance'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Balance Results */}
      {balanceData && (
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
              Balance Financiero - {getStoreName(balanceData.store_id)} 
              ({getMonthName(balanceData.period_month)} {balanceData.period_year})
            </h3>
            
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-900">
                  {formatCurrency(balanceData.initial_inventory_usd)}
                </div>
                <div className="text-sm text-blue-600">Inventario Inicial</div>
                <div className="text-xs text-gray-500">
                  Bs. {balanceData.initial_inventory_ves.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                </div>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-red-900">
                  {formatCurrency(balanceData.total_debt_usd)}
                </div>
                <div className="text-sm text-red-600">Deudas Pendientes</div>
                <div className="text-xs text-gray-500">
                  Bs. {balanceData.total_debt_ves.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                </div>
              </div>
              
              <div className={`p-4 rounded-lg ${balanceData.net_equity_usd >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className={`text-2xl font-bold ${balanceData.net_equity_usd >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                  {formatCurrency(balanceData.net_equity_usd)}
                </div>
                <div className={`text-sm ${balanceData.net_equity_usd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  Capital Propio
                </div>
                <div className="text-xs text-gray-500">
                  Bs. {balanceData.net_equity_ves.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                </div>
              </div>
              
              <div className="p-4 rounded-lg bg-purple-50">
                <div className="text-2xl font-bold text-purple-900">
                  {balanceData.debt_to_inventory_ratio.toFixed(1)}%
                </div>
                <div className="text-sm text-purple-600">Ratio Deuda/Inventario</div>
                <div className={`text-xs px-2 py-1 rounded-full mt-1 inline-block ${getHealthColor(balanceData.financial_health)}`}>
                  {balanceData.financial_health}
                </div>
              </div>
            </div>

            {/* Financial Health Indicator */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Interpretación del Balance</h4>
              <div className="text-sm text-gray-600">
                <div className="mb-1">
                  <strong>Ratio Capital Propio:</strong> {balanceData.equity_ratio.toFixed(1)}%
                </div>
                <div className="mb-1">
                  <strong>Estado Financiero:</strong> 
                  <span className={`ml-1 px-2 py-1 rounded text-xs ${getHealthColor(balanceData.financial_health)}`}>
                    {balanceData.financial_health}
                  </span>
                </div>
                <div className="text-xs mt-2">
                  {balanceData.financial_health === 'Healthy' && "✅ Excelente: La tienda tiene un buen equilibrio financiero"}
                  {balanceData.financial_health === 'Caution' && "⚠️ Precaución: Monitorear la relación deuda/inventario"}
                  {balanceData.financial_health === 'Critical' && "🚨 Crítico: La deuda supera significativamente el valor del inventario"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Initial Inventory Values Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Valores Iniciales de Inventario Registrados
          </h3>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-600">Cargando valores...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tienda
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Período
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor Inicial
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Moneda
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Notas
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inventoryValues.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {getStoreName(item.store_id)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getMonthName(item.period_month)} {item.period_year}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">${item.initial_value_usd.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                          <div className="text-xs text-gray-500">
                            Bs. {item.initial_value_ves.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          item.currency === 'USD' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {item.currency}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {item.notes || 'Sin notas'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => openEditModal(item)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200"
                          >
                            <PencilIcon className="h-3 w-3 mr-1" />
                            Editar
                          </button>
                          <button
                            onClick={() => handleDeleteInventoryValue(
                              item.id, 
                              getStoreName(item.store_id), 
                              item.period_month, 
                              item.period_year
                            )}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200"
                          >
                            <TrashIcon className="h-3 w-3 mr-1" />
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {inventoryValues.length === 0 && (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-sm text-gray-500">
                        No hay valores iniciales de inventario registrados.
                        <br />
                        <button
                          onClick={() => setShowCreateModal(true)}
                          className="mt-2 text-blue-600 hover:text-blue-500"
                        >
                          Registrar el primero
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Registrar Valor Inicial de Inventario
              </h3>
              
              <form onSubmit={handleCreateInventoryValue} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Tienda *
                  </label>
                  <select
                    required
                    value={newInventoryValue.store_id}
                    onChange={(e) => setNewInventoryValue({...newInventoryValue, store_id: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Seleccionar tienda</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Mes *
                    </label>
                    <select
                      required
                      value={newInventoryValue.period_month}
                      onChange={(e) => setNewInventoryValue({...newInventoryValue, period_month: parseInt(e.target.value)})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      {months.map(month => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Año *
                    </label>
                    <select
                      required
                      value={newInventoryValue.period_year}
                      onChange={(e) => setNewInventoryValue({...newInventoryValue, period_year: parseInt(e.target.value)})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      {[2024, 2025, 2026].map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Valor Inicial del Inventario *
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-1">
                      <select
                        value={newInventoryValue.currency}
                        onChange={(e) => setNewInventoryValue({...newInventoryValue, currency: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="USD">USD ($)</option>
                        <option value="VES">VES (Bs.)</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        step="0.01"
                        required
                        value={newInventoryValue.initial_value}
                        onChange={(e) => setNewInventoryValue({...newInventoryValue, initial_value: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder={newInventoryValue.currency === 'USD' ? '5000.00' : '750000.00'}
                      />
                    </div>
                  </div>
                  {newInventoryValue.initial_value && exchangeRate && (
                    <div className="mt-1 text-sm text-gray-600">
                      {newInventoryValue.currency === 'USD' ? (
                        <span>≈ Bs. {(parseFloat(newInventoryValue.initial_value || 0) * exchangeRate).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      ) : (
                        <span>≈ ${(parseFloat(newInventoryValue.initial_value || 0) / exchangeRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Notas (Opcional)
                  </label>
                  <textarea
                    value={newInventoryValue.notes}
                    onChange={(e) => setNewInventoryValue({...newInventoryValue, notes: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Notas adicionales sobre el inventario inicial..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={createLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {createLoading ? 'Registrando...' : 'Registrar Valor'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Editar Valor Inicial - {getStoreName(editingData.store_id)}
              </h3>
              
              <form onSubmit={handleEditInventoryValue} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Tienda *
                  </label>
                  <select
                    required
                    value={editInventoryValue.store_id}
                    onChange={(e) => setEditInventoryValue({...editInventoryValue, store_id: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Mes *
                    </label>
                    <select
                      required
                      value={editInventoryValue.period_month}
                      onChange={(e) => setEditInventoryValue({...editInventoryValue, period_month: parseInt(e.target.value)})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      {months.map(month => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Año *
                    </label>
                    <select
                      required
                      value={editInventoryValue.period_year}
                      onChange={(e) => setEditInventoryValue({...editInventoryValue, period_year: parseInt(e.target.value)})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      {[2024, 2025, 2026].map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Valor Inicial del Inventario *
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-1">
                      <select
                        value={editInventoryValue.currency}
                        onChange={(e) => setEditInventoryValue({...editInventoryValue, currency: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="USD">USD ($)</option>
                        <option value="VES">VES (Bs.)</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        step="0.01"
                        required
                        value={editInventoryValue.initial_value}
                        onChange={(e) => setEditInventoryValue({...editInventoryValue, initial_value: e.target.value})}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  {editInventoryValue.initial_value && exchangeRate && (
                    <div className="mt-1 text-sm text-gray-600">
                      {editInventoryValue.currency === 'USD' ? (
                        <span>≈ Bs. {(parseFloat(editInventoryValue.initial_value || 0) * exchangeRate).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      ) : (
                        <span>≈ ${(parseFloat(editInventoryValue.initial_value || 0) / exchangeRate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Notas (Opcional)
                  </label>
                  <textarea
                    value={editInventoryValue.notes}
                    onChange={(e) => setEditInventoryValue({...editInventoryValue, notes: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingData(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {editLoading ? 'Actualizando...' : 'Actualizar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InitialInventoryValues;